import {GROUND_BACKGROUNDS} from './groundBackgrounds';
import {GROUND_BACKGROUND_PREVIEW_PATHS} from './groundBackgroundPreviewPaths';

/**
 * Выбор подложки поля игроком — перед локальной партией (см.
 * HotSeatPlayersInitPage и pages/GroundBackgroundSelectPage). Здесь то, что
 * нужно только экрану выбора: названия подложек, порядок их показа и превью.
 * Сам перечень подложек — в configs/groundBackgrounds (он общий с сервером,
 * поэтому сюда вынесено все, что серверу не нужно).
 *
 * Значение выбора — идентификатор подложки или null: случайная, как и было
 * до появления выбора.
 *
 * ИГРОКУ ПОДЛОЖКА НАЗЫВАЕТСЯ «БИОМ» (решение владельца игры): слова, которые
 * он видит, собраны здесь — GROUND_BACKGROUND_CHOICE_TEXT. В коде и
 * комментариях она по-прежнему подложка.
 *
 * НОВОЙ ПОДЛОЖКЕ нужно: название в GROUND_BACKGROUND_NAMES, место в
 * GROUND_BACKGROUND_CHOICE_ORDER и превью (скрипт
 * developer_artifacts/scripts/generateGroundBackgroundPreviews.cjs). Снятую с
 * раздачи подложку убирают отсюда вместе с ключом в GROUND_BACKGROUNDS: ссылка
 * на закомментированный ключ превращается в ключ undefined. На все это есть
 * тест — молча не пройдет.
 */
export const RANDOM_GROUND_BACKGROUND_NAME = 'Случайный';

/** Слова выбора подложки, которые видит игрок */
export const GROUND_BACKGROUND_CHOICE_TEXT = Object.freeze({
  // Строка на странице настройки партии: «Биом: Пекло»
  label: 'Биом',
  pageTitle: 'Выбор биома',
  tooltipRandom: 'Биом поля будет выбран случайно. Нажмите, чтобы выбрать самостоятельно.',
  getTooltipChosen: (name) => `Биом поля: ${name}. Нажмите, чтобы изменить.`,
});

/**
 * Названия подложек — как их видит игрок. У каждой свое, без номеров (просьба
 * владельца игры): похожие подложки названы по тому, чем отличаются на
 * картинке, — лес вокруг, снежные вершины, реки, море
 */
export const GROUND_BACKGROUND_NAMES = Object.freeze({
  [GROUND_BACKGROUNDS.GREEN_1]: 'Лесная долина',
  [GROUND_BACKGROUNDS.GREEN_2]: 'Альпийские луга',
  [GROUND_BACKGROUNDS.GREEN_3]: 'Золотая степь',
  [GROUND_BACKGROUNDS.GREEN_4]: 'Речная пойма',
  [GROUND_BACKGROUNDS.PLATO_1]: 'Плато',
  [GROUND_BACKGROUNDS.RICE_1]: 'Рисовые террасы',
  [GROUND_BACKGROUNDS.ISLAND_1]: 'Остров',
  [GROUND_BACKGROUNDS.MAGICAL_FOREST_2]: 'Волшебный лес',
  [GROUND_BACKGROUNDS.MUSHROOM_1]: 'Грибной лес',
  [GROUND_BACKGROUNDS.BOG_1]: 'Болото',
  [GROUND_BACKGROUNDS.DESERT_1]: 'Оазисы',
  [GROUND_BACKGROUNDS.DESERT_2]: 'Дюны',
  [GROUND_BACKGROUNDS.RED_1]: 'Красный каньон',
  [GROUND_BACKGROUNDS.BONE_1]: 'Костяная пустошь',
  [GROUND_BACKGROUNDS.SPIRAL_1]: 'Каменные спирали',
  [GROUND_BACKGROUNDS.SALT_1]: 'Солончак',
  [GROUND_BACKGROUNDS.GEYSER_1]: 'Гейзеры',
  [GROUND_BACKGROUNDS.WINTER_1]: 'Тайга',
  [GROUND_BACKGROUNDS.WINTER_2]: 'Зимние сумерки',
  [GROUND_BACKGROUNDS.WINTER_3]: 'Замерзшие реки',
  [GROUND_BACKGROUNDS.WINTER_4]: 'Снежные вершины',
  [GROUND_BACKGROUNDS.WINTER_5]: 'Льды',
  [GROUND_BACKGROUNDS.ASH_1]: 'Пепелище',
  [GROUND_BACKGROUNDS.VOLCANO_1]: 'Вулкан',
  [GROUND_BACKGROUNDS.DEMON_1]: 'Пекло',
});

/**
 * Порядок подложек на экране выбора: похожие рядом — зелень, пески, снега,
 * темные. Он же порядок названий выше
 */
export const GROUND_BACKGROUND_CHOICE_ORDER = Object.freeze(Object.keys(GROUND_BACKGROUND_NAMES));

const checkIfOwnKey = (map, key) => typeof key === 'string' && Object.prototype.hasOwnProperty.call(map, key);

/**
 * Название выбора: подложки — ее название, всего остального (null, снятая с
 * раздачи подложка из сохраненного выбора) — «Случайный»: такой выбор и
 * разыгрывается случайно
 * @param {string|null} [groundBackgroundId]
 * @returns {string}
 */
export const getGroundBackgroundChoiceName = (groundBackgroundId) => (
  checkIfOwnKey(GROUND_BACKGROUND_NAMES, groundBackgroundId)
    ? GROUND_BACKGROUND_NAMES[groundBackgroundId]
    : RANDOM_GROUND_BACKGROUND_NAME
);

/**
 * Маленькая картинка подложки для экрана выбора. Полную картинку подложки
 * (4096x4096) в меню показывать нельзя
 * @param {string|null} [groundBackgroundId]
 * @returns {string|null} путь превью; null — превью нет (в том числе у случайной)
 */
export const getGroundBackgroundPreview = (groundBackgroundId) => (
  checkIfOwnKey(GROUND_BACKGROUND_PREVIEW_PATHS, groundBackgroundId)
    ? GROUND_BACKGROUND_PREVIEW_PATHS[groundBackgroundId]
    : null
);
